import { COLORS } from '../shared/colors';
import type { Settings } from './settings';

const VOICE_CLIPS = [
  ...COLORS.flatMap((color) => [
    `/audio/voice/zh-${color.id}.wav`,
    `/audio/voice/en-${color.id}.wav`,
  ]),
  '/audio/lore/m06-leak-zh.wav',
];

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private voiceBus: GainNode | null = null;
  private ambience: GainNode | null = null;
  private hum: OscillatorNode | null = null;
  private humGain: GainNode | null = null;
  private noiseSource: AudioBufferSourceNode | null = null;
  private noiseGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private voiceBuffers = new Map<string, AudioBuffer>();
  private loreLeakSource: AudioBufferSourceNode | null = null;
  private horrorActive = false;
  private horrorTimer: number | null = null;
  private horrorIntensity = 0;
  private preloaded = false;
  private heartbeatTimer: number | null = null;
  private pollution = 0;
  private settings: Settings;
  private initialized = false;

  constructor(settings: Settings) {
    this.settings = settings;
  }

  get ready(): boolean {
    return this.initialized && this.ctx !== null;
  }

  async init(): Promise<void> {
    if (this.ctx) {
      await this.ctx.resume().catch(() => {});
      return;
    }
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    try {
      const ctx = new Ctor();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.gain.value = this.settings.masterVolume;
      this.master.connect(ctx.destination);

      this.sfxBus = ctx.createGain();
      this.sfxBus.gain.value = this.settings.effectsVolume;
      this.sfxBus.connect(this.master);

      this.voiceBus = ctx.createGain();
      this.voiceBus.gain.value = 0.9;
      this.voiceBus.connect(this.master);

      this.noiseBuffer = this.createNoiseBuffer(ctx);
      this.startAmbience();
      this.initialized = true;
      this.prewarmHeartbeat();
      await ctx.resume().catch(() => {});
      void this.preloadVoices();
    } catch {
      this.ctx = null;
    }
  }

  setSettings(settings: Settings): void {
    this.settings = settings;
    if (this.ctx && this.master && this.sfxBus) {
      this.master.gain.setTargetAtTime(settings.masterVolume, this.ctx.currentTime, 0.04);
      this.sfxBus.gain.setTargetAtTime(settings.effectsVolume, this.ctx.currentTime, 0.04);
    }
  }

  async resume(): Promise<void> {
    if (this.ctx?.state === 'suspended') {
      await this.ctx.resume().catch(() => {});
    }
  }

  setPollution(level: number): void {
    this.pollution = Math.max(0, Math.min(100, level));
    if (this.ctx && this.ambience && this.humGain && this.noiseGain) {
      const now = this.ctx.currentTime;
      this.ambience.gain.setTargetAtTime(0.55 + this.pollution / 140, now, 0.25);
      this.humGain.gain.setTargetAtTime(0.02 + this.pollution * 0.00028, now, 0.4);
      this.noiseGain.gain.setTargetAtTime(0.008 + this.pollution * 0.00016, now, 0.4);
    }
    this.scheduleHeartbeat();
  }

  playBoot(): void {
    if (!this.ctx || !this.sfxBus) return;
    const now = this.ctx.currentTime;
    this.tone(58, 1.1, 'sine', 0.12, now, 110);
    this.noiseBurst(0.6, 0.04, 'lowpass', 600, now);
    this.tone(880, 0.04, 'square', 0.05, now + 0.12, 440);
    this.tone(440, 0.04, 'square', 0.04, now + 0.2, 220);
  }

  playTick(accent = false): void {
    if (!this.ctx) return;
    this.tone(accent ? 760 : 520, 0.035, 'square', accent ? 0.07 : 0.04, this.ctx.currentTime + 0.001, 180);
  }

  playClick(): void {
    if (!this.ctx) return;
    this.tone(1100, 0.025, 'square', 0.025, this.ctx.currentTime, 620);
  }

  playCorrect(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(540, 0.07, 'triangle', 0.08, now, 660);
    this.tone(810, 0.12, 'triangle', 0.07, now + 0.07, 920);
  }

  playWrong(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(140, 0.3, 'sawtooth', 0.09, now, 62);
    this.noiseBurst(0.22, 0.1, 'bandpass', 900, now);
    this.tone(62, 0.16, 'sine', 0.16, now + 0.02, 40);
  }

  playShock(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.noiseBurst(0.14, 0.2, 'highpass', 1800, now);
    this.noiseBurst(0.28, 0.12, 'bandpass', 2200, now + 0.08);
    this.tone(48, 0.35, 'sine', 0.24, now, 28);
    this.tone(250, 0.06, 'square', 0.08, now + 0.04, 90);
  }

  playGlitch(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.noiseBurst(0.13, 0.06, 'highpass', 1200, now);
    this.tone(90, 0.15, 'sawtooth', 0.04, now, 620);
  }

  playProtocol(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(110, 0.7, 'sine', 0.1, now, 55);
    this.tone(165, 0.55, 'triangle', 0.05, now + 0.04, 82);
    this.noiseBurst(0.3, 0.04, 'lowpass', 500, now);
  }

  playSuddenDeath(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(55, 1.4, 'sawtooth', 0.11, now, 30);
    this.tone(220, 0.7, 'square', 0.04, now, 60);
    this.noiseBurst(0.8, 0.1, 'lowpass', 350, now);
  }

  playMatchEnd(won: boolean): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    if (won) {
      this.tone(220, 0.35, 'triangle', 0.09, now);
      this.tone(330, 0.35, 'triangle', 0.08, now + 0.22);
      this.tone(440, 0.6, 'triangle', 0.07, now + 0.44);
    } else {
      this.tone(120, 0.9, 'sawtooth', 0.08, now, 48);
      this.tone(80, 1.2, 'sine', 0.12, now + 0.15, 38);
      this.noiseBurst(0.7, 0.05, 'lowpass', 300, now + 0.1);
    }
  }

  playVoice(clip: string): void {
    if (!this.ctx || !this.voiceBus) return;
    const buffer = this.voiceBuffers.get(clip);
    if (!buffer) {
      void this.loadVoice(clip).then((loaded) => {
        if (loaded) this.playVoice(clip);
      });
      return;
    }
    const now = this.ctx.currentTime + 0.02;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = 0.96 + Math.random() * 0.08;
    source.detune.value = -20 + Math.random() * 40;

    const band = this.ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 1250;
    band.Q.value = 0.65;

    const lowpass = this.ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 3100;

    const shaper = this.ctx.createWaveShaper();
    shaper.curve = distortionCurve(8);
    shaper.oversample = '2x';

    const gain = this.ctx.createGain();
    gain.gain.value = 1.35;

    source.connect(band).connect(shaper).connect(lowpass).connect(gain).connect(this.voiceBus);
    source.start(now);
    this.noiseBurst(0.16, 0.035, 'highpass', 1700, now);
    this.tone(75, 0.14, 'sine', 0.02, now);
  }

  /** 大灯压下：接触器脱扣 + 灯丝衰减 */
  playLampPress(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(180, 0.07, 'square', 0.09, now, 70);
    this.noiseBurst(0.1, 0.07, 'bandpass', 2400, now);
    this.tone(96, 0.85, 'sawtooth', 0.05, now + 0.04, 34);
    this.tone(48, 1.1, 'sine', 0.07, now + 0.06, 26);
  }

  /** 大灯啪地打开：吸合 + 点亮嗡鸣 */
  playLampOn(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(120, 0.06, 'square', 0.11, now, 320);
    this.noiseBurst(0.13, 0.09, 'highpass', 1900, now);
    this.tone(64, 1.4, 'sine', 0.07, now + 0.05, 100);
    this.tone(2400, 0.5, 'sine', 0.012, now + 0.08, 2400);
  }

  /** 屏幕两次点亮：显像管消磁的两记闷响 */
  playScreenBoot(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const offset of [0, 0.34]) {
      this.tone(72, 0.2, 'sine', 0.13, now + offset, 58);
      this.noiseBurst(0.12, 0.05, 'bandpass', 520, now + offset);
      this.tone(1600, 0.09, 'square', 0.02, now + offset + 0.03, 900);
    }
  }

  /** 键盘就绪：一排继电器依次吸合 */
  playKeypadReady(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (let index = 0; index < 8; index += 1) {
      this.tone(900 + index * 40, 0.03, 'square', 0.03, now + index * 0.045, 420);
    }
    this.tone(140, 0.18, 'triangle', 0.05, now + 0.36, 220);
  }

  /** 单向镜转透：对面房间的低频 + 玻璃泛音 */
  playGlassReveal(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(44, 1.6, 'sine', 0.08, now, 58);
    this.noiseBurst(1.1, 0.022, 'lowpass', 420, now);
    this.tone(1860, 0.7, 'sine', 0.014, now + 0.15, 1720);
  }

  /** 血量灯亮起：一根灯管的接通 */
  playBulbOn(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.tone(2400, 0.06, 'sine', 0.02, now, 2600);
    this.tone(110, 0.24, 'triangle', 0.035, now, 132);
  }

  /** 灯管烧掉：钨丝断裂的爆响 */
  playBulbOut(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.noiseBurst(0.07, 0.1, 'highpass', 3200, now);
    this.tone(2100, 0.05, 'square', 0.03, now, 700);
    this.tone(70, 0.4, 'sine', 0.06, now + 0.02, 42);
  }

  playLoreLeak(clip: string): void {
    if (!this.ctx || !this.voiceBus) return;
    const buffer = this.voiceBuffers.get(clip);
    if (!buffer) {
      void this.loadVoice(clip).then((loaded) => {
        if (loaded) this.playLoreLeak(clip);
      });
      return;
    }
    this.stopLoreLeak();
    const now = this.ctx.currentTime + 0.05;
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = 0.94;
    source.detune.value = -12;

    const lowpass = this.ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 1850;

    const gain = this.ctx.createGain();
    gain.gain.value = 0.42;

    source.connect(lowpass).connect(gain).connect(this.voiceBus);
    source.onended = () => {
      if (this.loreLeakSource === source) this.loreLeakSource = null;
    };
    source.start(now);
    this.loreLeakSource = source;
  }

  stopLoreLeak(): void {
    if (this.loreLeakSource) {
      try {
        this.loreLeakSource.stop();
      } catch {
        // already stopped
      }
      this.loreLeakSource = null;
    }
  }

  startHorror(): void {
    if (this.horrorActive) return;
    this.horrorActive = true;
    this.scheduleScare();
  }

  stopHorror(): void {
    this.horrorActive = false;
    if (this.horrorTimer !== null) {
      window.clearTimeout(this.horrorTimer);
      this.horrorTimer = null;
    }
  }

  setHorrorIntensity(value: number): void {
    this.horrorIntensity = Math.max(0, Math.min(100, Math.round(value)));
  }

  playScream(power = 1): void {
    if (!this.ctx || !this.sfxBus) return;
    const ctx = this.ctx;
    const now = ctx.currentTime + 0.02;
    const strength = Math.max(0.15, Math.min(1, power));
    try {
      const panner = ctx.createStereoPanner();
      panner.pan.value = (Math.random() * 2 - 1) * 0.65;
      const shaper = ctx.createWaveShaper();
      shaper.curve = distortionCurve(24);
      shaper.oversample = '2x';
      const lowpass = ctx.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.value = 3600;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.12 * strength + 0.025, now + 0.045);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.05);
      gain.connect(shaper).connect(lowpass).connect(panner).connect(this.sfxBus);

      for (const detune of [-17, 13]) {
        const oscillator = ctx.createOscillator();
        oscillator.type = 'sawtooth';
        oscillator.detune.value = detune;
        oscillator.frequency.setValueAtTime(240 + 70 * strength, now);
        oscillator.frequency.exponentialRampToValueAtTime(1350 + 500 * strength, now + 0.14);
        oscillator.frequency.exponentialRampToValueAtTime(760, now + 0.42);
        oscillator.frequency.exponentialRampToValueAtTime(170, now + 0.95);
        const voiceGain = ctx.createGain();
        voiceGain.gain.value = 0.42;
        oscillator.connect(voiceGain).connect(gain);
        oscillator.start(now);
        oscillator.stop(now + 1.08);
      }

      const source = ctx.createBufferSource();
      source.buffer = this.noiseBuffer;
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(1800, now);
      filter.frequency.exponentialRampToValueAtTime(950, now + 0.8);
      filter.Q.value = 1.8;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(0.0001, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.05 * strength + 0.01, now + 0.03);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);
      source.connect(filter).connect(noiseGain).connect(panner);
      source.start(now);
      source.stop(now + 0.94);
    } catch {
      // audio failures should never break the game
    }
  }

  playWhisper(power = 0.5): void {
    if (!this.ctx || !this.sfxBus || !this.noiseBuffer) return;
    const ctx = this.ctx;
    const now = ctx.currentTime + 0.02;
    try {
      const source = ctx.createBufferSource();
      source.buffer = this.noiseBuffer;
      source.loop = true;
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = 1150 + Math.random() * 900;
      band.Q.value = 2.6;
      const panner = ctx.createStereoPanner();
      panner.pan.value = (Math.random() * 2 - 1) * 0.8;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.035 + power * 0.03, now + 0.18);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.8);
      const tremolo = ctx.createOscillator();
      tremolo.frequency.value = 5.5 + Math.random() * 3;
      const tremoloGain = ctx.createGain();
      tremoloGain.gain.value = 0.02;
      tremolo.connect(tremoloGain).connect(gain.gain);
      source.connect(band).connect(gain).connect(panner).connect(this.sfxBus);
      source.start(now);
      source.stop(now + 1.9);
      tremolo.start(now);
      tremolo.stop(now + 1.9);
    } catch {
      // ignore
    }
  }

  playCreak(power = 0.5): void {
    if (!this.ctx || !this.sfxBus || !this.noiseBuffer) return;
    const ctx = this.ctx;
    const now = ctx.currentTime + 0.02;
    try {
      const source = ctx.createBufferSource();
      source.buffer = this.noiseBuffer;
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.setValueAtTime(420, now);
      band.frequency.exponentialRampToValueAtTime(120, now + 0.7);
      band.Q.value = 4;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.03 + power * 0.03, now + 0.06);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.85);
      source.connect(band).connect(gain).connect(this.sfxBus);
      source.start(now);
      source.stop(now + 0.9);
      this.tone(74 + Math.random() * 28, 0.65, 'sine', 0.025 + power * 0.025, now, 42);
    } catch {
      // ignore
    }
  }

  private scheduleScare(): void {
    if (!this.horrorActive) return;
    if (this.horrorTimer !== null) window.clearTimeout(this.horrorTimer);
    const intensity = this.horrorIntensity;
    const delay = 9000 - intensity * 55 + Math.random() * 4200;
    this.horrorTimer = window.setTimeout(() => {
      this.horrorTimer = null;
      if (!this.horrorActive || document.hidden) {
        this.scheduleScare();
        return;
      }
      const roll = Math.random();
      const power = 0.25 + intensity / 140;
      if (roll < 0.36) this.playScream(power);
      else if (roll < 0.72) this.playWhisper(power);
      else this.playCreak(power);
      this.scheduleScare();
    }, Math.max(2800, delay));
  }

  private async preloadVoices(): Promise<void> {
    if (this.preloaded) return;
    this.preloaded = true;
    await Promise.all(VOICE_CLIPS.map((clip) => this.loadVoice(clip)));
  }

  private async loadVoice(clip: string): Promise<boolean> {
    if (this.voiceBuffers.has(clip)) return true;
    if (!this.ctx) return false;
    try {
      const response = await fetch(clip);
      if (!response.ok) return false;
      const data = await response.arrayBuffer();
      const buffer = await this.ctx.decodeAudioData(data);
      this.voiceBuffers.set(clip, buffer);
      return true;
    } catch {
      return false;
    }
  }

  private startAmbience(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    this.ambience = ctx.createGain();
    this.ambience.gain.value = 0.55;
    this.ambience.connect(this.master);

    this.hum = ctx.createOscillator();
    this.hum.type = 'sine';
    this.hum.frequency.value = 50;
    this.humGain = ctx.createGain();
    this.humGain.gain.value = 0.02;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 140;
    this.hum.connect(this.humGain).connect(lowpass).connect(this.ambience);
    this.hum.start();

    if (this.noiseBuffer) {
      this.noiseSource = ctx.createBufferSource();
      this.noiseSource.buffer = this.noiseBuffer;
      this.noiseSource.loop = true;
      this.noiseGain = ctx.createGain();
      this.noiseGain.gain.value = 0.008;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'lowpass';
      noiseFilter.frequency.value = 420;
      this.noiseSource.connect(noiseFilter).connect(this.noiseGain).connect(this.ambience);
      this.noiseSource.start();
    }
  }

  private scheduleHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      window.clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (!this.ctx || !this.initialized || this.settings.masterVolume <= 0) return;
    const interval = this.pollution >= 90 ? 400 : this.pollution >= 70 ? 520 : this.pollution >= 40 ? 720 : 1050;
    const beat = () => {
      this.thump();
      this.heartbeatTimer = window.setTimeout(beat, interval + Math.random() * 80);
    };
    this.heartbeatTimer = window.setTimeout(beat, interval);
  }

  private prewarmHeartbeat(): void {
    this.scheduleHeartbeat();
  }

  private thump(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const strength = 0.045 + (this.pollution / 100) * 0.12;
    this.tone(54, 0.18, 'sine', strength, now, 34);
    this.tone(36, 0.24, 'sine', strength * 1.15, now + 0.02, 26);
    this.tone(48, 0.14, 'sine', strength * 0.7, now + 0.17, 30);
    this.noiseBurst(0.08, 0.018 + this.pollution * 0.0002, 'lowpass', 180, now);
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    gainValue: number,
    startAt: number,
    endFrequency?: number,
  ): void {
    if (!this.ctx || !this.sfxBus) return;
    try {
      const oscillator = this.ctx.createOscillator();
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(Math.max(20, frequency), startAt);
      if (endFrequency !== undefined) {
        oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), startAt + duration);
      }
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainValue), startAt + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
      oscillator.connect(gain).connect(this.sfxBus);
      oscillator.start(startAt);
      oscillator.stop(startAt + duration + 0.03);
    } catch {
      // audio node errors should never break the game
    }
  }

  private noiseBurst(
    duration: number,
    gainValue: number,
    filterType: BiquadFilterType,
    frequency: number,
    startAt: number,
  ): void {
    if (!this.ctx || !this.sfxBus || !this.noiseBuffer) return;
    try {
      const source = this.ctx.createBufferSource();
      source.buffer = this.noiseBuffer;
      const filter = this.ctx.createBiquadFilter();
      filter.type = filterType;
      filter.frequency.value = frequency;
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, gainValue), startAt + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
      source.connect(filter).connect(gain).connect(this.sfxBus);
      source.start(startAt);
      source.stop(startAt + duration + 0.02);
    } catch {
      // ignore
    }
  }

  private createNoiseBuffer(ctx: AudioContext): AudioBuffer {
    const length = Math.floor(ctx.sampleRate * 2);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < length; i += 1) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    return buffer;
  }
}

function distortionCurve(amount: number): Float32Array<ArrayBuffer> {
  const samples = 1024;
  const curve = new Float32Array(samples);
  const k = amount;
  for (let i = 0; i < samples; i += 1) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((1 + k) * x) / (1 + k * Math.abs(x));
  }
  return curve;
}
